import { z } from "zod";
import { TRAFFIC_LIGHT_STATUSES, type TrafficLightStatus } from "@dd-report-components";

export const dueDiligenceCaseStatusSchema = z.enum([
  "draft",
  "in_progress",
  "under_review",
  "completed",
  "archived",
  "pending_payment"
]);

export type DueDiligenceCaseStatus = z.infer<typeof dueDiligenceCaseStatusSchema>;

export const dueDiligenceTierSchema = z.enum(['basic', 'professional', 'premium']);
export type DueDiligenceTier = z.infer<typeof dueDiligenceTierSchema>;

export const dueDiligenceOperationTypeSchema = z.enum([
  'acquisition_investment',
  'divestment_sale',
  'land_development',
  'other'
]);
export type DueDiligenceOperationType = z.infer<typeof dueDiligenceOperationTypeSchema>;

export const DueDiligenceCaseStatuses = dueDiligenceCaseStatusSchema.enum;

const DUE_DILIGENCE_CASE_STATUS_LABELS: Record<DueDiligenceCaseStatus, string> = {
  draft: "Borrador",
  in_progress: "En Progreso",
  under_review: "En Revisión",
  completed: "Completado",
  archived: "Archivado",
  pending_payment: "Pendiente de Pago"
};

export const getDueDiligenceCaseStatusLabel = (status: string): string =>
  DUE_DILIGENCE_CASE_STATUS_LABELS[status as DueDiligenceCaseStatus] ?? status;

export const trafficLightStatusSchema = z.enum(TRAFFIC_LIGHT_STATUSES);

export type { TrafficLightStatus };

export const createDueDiligenceCaseRequestSchema = z.object({
  name: z.string().min(1),
  propertyAddress: z.string().optional(),
  propertyType: z.string().optional(),
  cadastralReference: z.string().optional(),
  tier: dueDiligenceTierSchema,
  observations: z.string().optional(),
  operationType: dueDiligenceOperationTypeSchema,
  ownerName: z.string().optional(),
  ownerIdCard: z.string().optional(),
});

export type CreateDueDiligenceCaseRequest = z.infer<typeof createDueDiligenceCaseRequestSchema>;

export const updateDueDiligenceCaseRequestSchema = z.object({
  name: z.string().optional(),
  propertyAddress: z.string().optional(),
  propertyType: z.string().optional(),
  cadastralReference: z.string().optional(),
  status: dueDiligenceCaseStatusSchema.optional()
});

export type UpdateDueDiligenceCaseRequest = z.infer<typeof updateDueDiligenceCaseRequestSchema>;

export const dueDiligenceCaseSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  businessName: z.string(),
  name: z.string(),
  propertyAddress: z.string().nullable(),
  propertyType: z.string().nullable(),
  cadastralReference: z.string().nullable(),
  status: dueDiligenceCaseStatusSchema,
  alias: z.string(),
  finalStatement: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  tier: dueDiligenceTierSchema.nullable(),
  trafficLightStatus: trafficLightStatusSchema.nullable().optional(),
  price: z.number().nullable().optional(),
  invoiceUrl: z.string().nullable().optional(),
  previousStatus: dueDiligenceCaseStatusSchema.nullable().optional(),
  observations: z.string().nullable().optional(),
  operationType: dueDiligenceOperationTypeSchema.nullable().optional(),
  ownerName: z.string().nullable().optional(),
  ownerIdCard: z.string().nullable().optional()
});

export type DueDiligenceCase = z.infer<typeof dueDiligenceCaseSchema>;

// Document Types
export const documentTypeSchema = z.enum([
  'base_document',
  'additional_document',
  'contract',
  'cadastral',
  'technical_report',
  'legal_document',
  'financial_document',
  'report_legal_fiscal_source',
  'urbanistic_document',
  'other'
]);

export type DocumentType = z.infer<typeof documentTypeSchema>;

export const documentAnalysisStatusSchema = z.enum([
  'pending',
  'pending_approval',
  'analyzing',
  'completed',
  'failed'
]);

export type DocumentAnalysisStatus = z.infer<typeof documentAnalysisStatusSchema>;

export const documentReviewStatusSchema = z.enum([
  'en_revision',
  'revisado'
]);

export type DocumentReviewStatus = z.infer<typeof documentReviewStatusSchema>;

export const dueDiligenceDocumentSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  documentType: documentTypeSchema,
  fileName: z.string(),
  filePath: z.string().optional(),
  fileSize: z.number(),
  mimeType: z.string(),
  uploadDate: z.string().datetime(),
  uploadedBy: z.string().optional(),
  publicName: z.string().nullable().optional(),
  analysisStatus: documentAnalysisStatusSchema.optional(),
  reviewStatus: documentReviewStatusSchema.optional().default('en_revision'),
  trafficLightStatus: trafficLightStatusSchema.nullable().optional(),
  visibleToClient: z.boolean().optional(),
});

export type DueDiligenceDocument = z.infer<typeof dueDiligenceDocumentSchema>;

// Legacy types for backwards compatibility
export enum DueDiligenceStatus {
  DRAFT = "draft",
  IN_PROGRESS = "in_progress",
  UNDER_REVIEW = "under_review",
  COMPLETED = "completed",
  ARCHIVED = "archived",
  PENDING_PAYMENT = "pending_payment"
}

export enum DueDiligenceResult {
  FAVORABLE = "FAVORABLE",
  WARNING = "WARNING",
  UNFAVORABLE = "UNFAVORABLE"
}

// A note the client pins to a section of the report. Business-wide, not
// private: a teammate sees it and can edit it, which is why the author
// travels with it.
export const dueDiligenceReportNoteSchema = z.object({
  id: z.string().uuid(),
  sectionNumber: z.number().int().positive(),
  content: z.string(),
  authorId: z.string(),
  authorName: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type DueDiligenceReportNote = z.infer<typeof dueDiligenceReportNoteSchema>;

// Mirrors the controller's own cap, so an over-long note is refused in the
// textarea rather than by a 400.
export const REPORT_NOTE_MAX_LENGTH = 2000;
